import { Copy, Lock, MoreVertical, Pencil, RefreshCw, Unlock } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/auth';
import { useToast } from '@/hooks/use-toast';
import { normalizeApiError } from '@/lib/errors';
import { authService } from '@/services/auth';
import { userService } from '@/services/user';
import { UserListItem } from '@/types/user';
import {
  canActOnUser,
  getDisabledUserActionTooltip,
} from '@/utils/rbac';

type UserRowActionsProps = {
  user: UserListItem;
  onEdit: (_user: UserListItem) => void;
  onBlock: (_user: UserListItem) => void;
  onUnblock: (_user: UserListItem) => void;
  onInviteGenerated?: (_inviteUrl: string) => void;
};

type InviteActionMode = 'hidden' | 'loading' | 'copy' | 'renew' | 'email';

const inviteUrlByUserId = new Map<string, string>();

const getInviteReason = (error: { details?: unknown }): string | undefined => {
  const details = error.details;
  if (details && typeof details === 'object') {
    const reason = (details as { reason?: unknown }).reason;
    if (typeof reason === 'string') return reason;
  }
  return undefined;
};

export const UserRowActions = ({
  user,
  onEdit,
  onBlock,
  onUnblock,
  onInviteGenerated,
}: UserRowActionsProps) => {
  const { user: currentUser } = useAuth();
  const { toast } = useToast();
  const canAct = canActOnUser(currentUser, user);
  const tooltip = getDisabledUserActionTooltip(currentUser, user);
  const isBlocked = user.status === 'BLOCKED' || user.status === 'INACTIVE';
  const isPending = user.status === 'PENDING';
  const [inviteMode, setInviteMode] = useState<InviteActionMode>(() => {
    if (!isPending) return 'hidden';
    return inviteUrlByUserId.has(user.id) ? 'copy' : 'loading';
  });
  const [inviteBusy, setInviteBusy] = useState(false);

  const fetchInviteLink = async (): Promise<string | null> => {
    try {
      const response = await userService.getInviteLink(user.id);
      inviteUrlByUserId.set(user.id, response.invite_url);
      setInviteMode('copy');
      return response.invite_url;
    } catch (error) {
      const normalized = normalizeApiError(error);
      const reason = getInviteReason(normalized);
      inviteUrlByUserId.delete(user.id);
      if (reason === 'ALREADY_ACTIVE' || normalized.status === 404) {
        setInviteMode('hidden');
        return null;
      }
      if (reason === 'NOT_A_CLIENT') {
        setInviteMode('email');
        return null;
      }
      if (
        normalized.status === 409 ||
        normalized.code === 'USER_INVITE_NOT_AVAILABLE' ||
        reason === 'EXPIRED'
      ) {
        setInviteMode('renew');
        return null;
      }
      setInviteMode('renew');
      return null;
    }
  };

  const resolveInviteMode = async () => {
    if (!canAct || !isPending) {
      setInviteMode('hidden');
      return;
    }

    setInviteMode('loading');
    await fetchInviteLink();
  };

  const copyInviteUrl = async (inviteUrl: string) => {
    await navigator.clipboard.writeText(inviteUrl);
    toast({
      title: 'Link copiado',
      description: 'O link de convite foi copiado para a área de transferência.',
    });
  };

  const handleCopyInvite = async () => {
    setInviteBusy(true);
    try {
      const inviteUrl = await fetchInviteLink();
      if (!inviteUrl) {
        toast({
          variant: 'error',
          title: 'Convite indisponível',
          description: 'Envie um novo convite para gerar o link.',
        });
        return;
      }
      await copyInviteUrl(inviteUrl);
    } catch (error) {
      const normalized = normalizeApiError(error);
      toast({
        variant: 'error',
        title: 'Não foi possível copiar o link',
        description: normalized.message,
      });
    } finally {
      setInviteBusy(false);
    }
  };

  const handleRenewInvite = async () => {
    setInviteBusy(true);
    try {
      await userService.createInviteLink(user.id);
      const inviteUrl = await fetchInviteLink();
      if (!inviteUrl) {
        toast({
          variant: 'error',
          title: 'Convite enviado, mas o link ainda não está disponível',
          description: 'Abra o menu novamente em instantes para copiar o link.',
        });
        return;
      }
      toast({
        title: 'Novo convite enviado',
        description: 'O link de convite foi gerado e já pode ser copiado.',
      });
      onInviteGenerated?.(inviteUrl);
    } catch (error) {
      const normalized = normalizeApiError(error);
      const reason = getInviteReason(normalized);
      if (reason === 'NOT_A_CLIENT') {
        setInviteMode('email');
        toast({
          variant: 'error',
          title: 'Convite por e-mail',
          description: 'Este usuário não possui link copiável. Use o envio por e-mail.',
        });
        return;
      }
      toast({
        variant: 'error',
        title: 'Não foi possível enviar o novo convite',
        description: normalized.message,
      });
    } finally {
      setInviteBusy(false);
    }
  };

  const handleEmailInvite = async () => {
    setInviteBusy(true);
    try {
      await authService.resendInvitation({ email: user.email });
      const inviteUrl = await fetchInviteLink();
      if (inviteUrl) {
        toast({
          title: 'Novo convite enviado',
          description: 'O link de convite foi gerado e já pode ser copiado.',
        });
        onInviteGenerated?.(inviteUrl);
        return;
      }
      toast({
        title: 'Novo convite enviado',
        description: `Enviamos o convite por e-mail para ${user.email}.`,
      });
    } catch (error) {
      const normalized = normalizeApiError(error);
      toast({
        variant: 'error',
        title: 'Não foi possível enviar o novo convite',
        description: normalized.message,
      });
    } finally {
      setInviteBusy(false);
    }
  };

  const trigger = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      disabled={!canAct}
      className="h-8 w-8 text-gray-700 disabled:pointer-events-none disabled:opacity-40"
      aria-label="Ações"
    >
      <MoreVertical className="h-4 w-4" />
    </Button>
  );

  if (!canAct) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">{trigger}</span>
          </TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const showCopy = isPending && (inviteMode === 'copy' || inviteMode === 'loading');
  const showRenew = isPending && inviteMode === 'renew';
  const showEmail = isPending && inviteMode === 'email';

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) {
          void resolveInviteMode();
        }
      }}
    >
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[193px]">
        <DropdownMenuItem onSelect={() => onEdit(user)} className="gap-2">
          <Pencil className="h-4 w-4" />
          Editar
        </DropdownMenuItem>
        {showCopy ? (
          <DropdownMenuItem
            disabled={inviteBusy || inviteMode === 'loading'}
            onSelect={(event) => {
              event.preventDefault();
              void handleCopyInvite();
            }}
            className="gap-2"
          >
            <Copy className="h-4 w-4" />
            {inviteMode === 'loading' ? 'Verificando convite...' : 'Copiar link do convite'}
          </DropdownMenuItem>
        ) : null}
        {showRenew ? (
          <DropdownMenuItem
            disabled={inviteBusy}
            onSelect={(event) => {
              event.preventDefault();
              void handleRenewInvite();
            }}
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Enviar novo convite
          </DropdownMenuItem>
        ) : null}
        {showEmail ? (
          <DropdownMenuItem
            disabled={inviteBusy}
            onSelect={(event) => {
              event.preventDefault();
              void handleEmailInvite();
            }}
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Enviar novo convite
          </DropdownMenuItem>
        ) : null}
        {isBlocked ? (
          <DropdownMenuItem onSelect={() => onUnblock(user)} className="gap-2">
            <Unlock className="h-4 w-4" />
            Desbloquear
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onSelect={() => onBlock(user)} className="gap-2">
            <Lock className="h-4 w-4" />
            Bloquear
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
